import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { ProtectedRoute } from './components/ProtectedRoute';
import { Dashboard } from './pages/Dashboard';
import { Information } from './pages/Information';
import { Login } from './pages/Login';
import { ResetPassword } from './pages/ResetPassword';
import { TransactionDetails } from './pages/TransactionDetails';
import { Transactions } from './pages/Transactions';
import { PAGE_ROUTE_REGISTRY } from './registry/pageRegistry';
import { getMe } from './services/authService';
import { useAuthStore } from './store/authStore';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30000,
    },
  },
});

const PAGE_COMPONENTS: Record<string, React.ComponentType> = {
  dashboard: Dashboard,
  transactions: Transactions,
  information: Information,
};

export default function App() {
  const { token, setUser, clearAuth } = useAuthStore();
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    if (!token) {
      setAuthReady(true);
      return;
    }
    const controller = new AbortController();
    getMe(controller.signal)
      .then((user) => setUser(user, user.requiresPasswordReset ?? false))
      .catch((err) => {
        if (!controller.signal.aborted) clearAuth();
      })
      .finally(() => {
        if (!controller.signal.aborted) setAuthReady(true);
      });
    return () => controller.abort();
  }, []);

  if (!authReady) return null;

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter basename={import.meta.env.BASE_URL}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {PAGE_ROUTE_REGISTRY.map(({ id, path }) => {
            const Component = PAGE_COMPONENTS[id];
            return (
              <Route
                key={id}
                path={path}
                element={
                  <ProtectedRoute>
                    <Component />
                  </ProtectedRoute>
                }
              />
            );
          })}
          <Route
            path="/transactions/:betTxnId"
            element={
              <ProtectedRoute>
                <TransactionDetails />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
